import {
  TEMPLATE_SCHEMA_VERSION,
  normalizeTemplateName,
  validateTemplateMetadata
} from "./template-contracts.js";

const LOCAL_STORAGE_PREFIX = "flexcharting.templates.v1";
const RECENT_STORAGE_PREFIX = "flexcharting.template-recents.v1";

export class TemplateRepositoryError extends Error {
  constructor(code, message, { currentVersion = null, status = 0 } = {}) {
    super(message);
    this.name = "TemplateRepositoryError";
    this.code = code;
    this.currentVersion = currentVersion;
    this.status = status;
  }
}

export class TemplateRepository {
  constructor({
    endpoint = null,
    type,
    storage = globalThis.localStorage,
    storageScope = "anonymous",
    recentLimit = 5
  }) {
    this.endpoint = endpoint || null;
    this.type = normalizeType(type);
    this.storage = storage;
    this.recentLimit = recentLimit;
    this.setStorageScope(storageScope);
  }

  setStorageScope(storageScope) {
    const scope = String(storageScope || "").trim();
    if (!scope) throw new TypeError("Template storage requires a product-qualified scope.");
    const suffix = `${encodeURIComponent(scope)}.${this.type.toLowerCase()}`;
    this.localStorageKey = `${LOCAL_STORAGE_PREFIX}.${suffix}`;
    this.recentStorageKey = `${RECENT_STORAGE_PREFIX}.${suffix}`;
  }

  async list() {
    if (!this.endpoint) return this.localRecords().map(summary);
    return this.request(`?type=${encodeURIComponent(this.type)}`);
  }

  async get(id) {
    if (!this.endpoint) {
      const record = this.localRecords().find((candidate) => candidate.id === id);
      if (!record) throw missing();
      return structuredClone(record);
    }
    return this.request(`/${encodeURIComponent(id)}`);
  }

  async create({ name, description, payload }) {
    const metadata = validateTemplateMetadata(name, description);
    if (!this.endpoint) {
      const records = this.localRecords();
      ensureUnique(records, this.type, metadata.normalizedName);
      const now = new Date().toISOString();
      const record = {
        id: createId(),
        tenantId: "local",
        userId: "local",
        type: this.type,
        name: metadata.name,
        normalizedName: metadata.normalizedName,
        description: metadata.description,
        schemaVersion: TEMPLATE_SCHEMA_VERSION,
        payload: structuredClone(payload),
        version: 1,
        createdAtUtc: now,
        updatedAtUtc: now
      };
      records.unshift(record);
      this.saveLocalRecords(records);
      return structuredClone(record);
    }
    return this.request("", {
      method: "POST",
      body: {
        type: this.type,
        name: metadata.name,
        description: metadata.description,
        schemaVersion: TEMPLATE_SCHEMA_VERSION,
        payload
      }
    });
  }

  async updateMetadata(id, { name, description, expectedVersion }) {
    const metadata = validateTemplateMetadata(name, description);
    if (!this.endpoint) {
      const records = this.localRecords();
      const record = owned(records, id, this.type);
      ensureVersion(record, expectedVersion);
      ensureUnique(records, this.type, metadata.normalizedName, id);
      Object.assign(record, metadata, {
        version: record.version + 1,
        updatedAtUtc: new Date().toISOString()
      });
      this.saveLocalRecords(records);
      return structuredClone(record);
    }
    return this.request(`/${encodeURIComponent(id)}/metadata`, {
      method: "PUT",
      body: {
        name: metadata.name,
        description: metadata.description,
        expectedVersion
      }
    });
  }

  async replacePayload(id, { payload, expectedVersion }) {
    if (!this.endpoint) {
      const records = this.localRecords();
      const record = owned(records, id, this.type);
      ensureVersion(record, expectedVersion);
      record.payload = structuredClone(payload);
      record.schemaVersion = TEMPLATE_SCHEMA_VERSION;
      record.version += 1;
      record.updatedAtUtc = new Date().toISOString();
      this.saveLocalRecords(records);
      return structuredClone(record);
    }
    return this.request(`/${encodeURIComponent(id)}/payload`, {
      method: "PUT",
      body: {
        schemaVersion: TEMPLATE_SCHEMA_VERSION,
        payload,
        expectedVersion
      }
    });
  }

  async duplicate(id, { name, description, expectedVersion }) {
    const metadata = validateTemplateMetadata(name, description);
    if (!this.endpoint) {
      const source = owned(this.localRecords(), id, this.type);
      ensureVersion(source, expectedVersion);
      return this.create({
        name: metadata.name,
        description: metadata.description,
        payload: source.payload
      });
    }
    return this.request(`/${encodeURIComponent(id)}/duplicate`, {
      method: "POST",
      body: {
        name: metadata.name,
        description: metadata.description,
        expectedVersion
      }
    });
  }

  async delete(id, expectedVersion) {
    if (!this.endpoint) {
      const records = this.localRecords();
      const record = owned(records, id, this.type);
      ensureVersion(record, expectedVersion);
      this.saveLocalRecords(records.filter((candidate) => candidate.id !== id));
      this.removeRecent(id);
      return;
    }
    await this.request(
      `/${encodeURIComponent(id)}?expectedVersion=${encodeURIComponent(expectedVersion)}`,
      { method: "DELETE" }
    );
    this.removeRecent(id);
  }

  markUsed(id) {
    const recents = this.readRecents().filter((candidate) => candidate !== id);
    recents.unshift(id);
    this.writeRecents(recents.slice(0, this.recentLimit));
  }

  recent(summaries) {
    const byId = new Map(summaries.map((item) => [item.id, item]));
    return this.readRecents().map((id) => byId.get(id)).filter(Boolean);
  }

  async request(path, { method = "GET", body = null } = {}) {
    const headers = { Accept: "application/json" };
    if (body !== null) headers["Content-Type"] = "application/json";
    if (method !== "GET") {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      if (!token) {
        throw new TemplateRepositoryError(
          "AuthenticationFailure",
          "The secure session token is unavailable. Reload the workspace and try again."
        );
      }
      headers["X-CSRF-TOKEN"] = token;
    }
    let response;
    try {
      response = await fetch(`${this.endpoint}${path}`, {
        method,
        headers,
        credentials: "same-origin",
        body: body === null ? undefined : JSON.stringify(body)
      });
    } catch {
      throw new TemplateRepositoryError(
        "Offline",
        "Templates are unavailable while the workspace is offline."
      );
    }
    if (response.ok) {
      if (response.status === 204) return null;
      return response.json();
    }
    let problem = null;
    try {
      problem = await response.json();
    } catch {
      // The stable fallback below is used for non-JSON proxy failures.
    }
    throw new TemplateRepositoryError(
      problem?.code || problem?.title || `Http${response.status}`,
      templatePolicyMessage(
        problem?.code,
        problem?.detail || "The template request could not be completed."
      ),
      {
        currentVersion: Number(problem?.currentVersion) || null,
        status: response.status
      }
    );
  }

  localRecords() {
    try {
      const parsed = JSON.parse(this.storage?.getItem(this.localStorageKey) || "[]");
      return Array.isArray(parsed)
        ? parsed.filter((record) => record?.type === this.type)
        : [];
    } catch {
      return [];
    }
  }

  saveLocalRecords(typeRecords) {
    try {
      this.storage?.setItem(this.localStorageKey, JSON.stringify(typeRecords));
    } catch {
      throw new TemplateRepositoryError(
        "StorageUnavailable",
        "This browser could not save the template locally."
      );
    }
  }

  readRecents() {
    return readJsonArray(this.storage, this.recentStorageKey);
  }

  writeRecents(ids) {
    try {
      this.storage?.setItem(this.recentStorageKey, JSON.stringify(ids));
    } catch {
      // Recent ordering is non-authoritative; template persistence still succeeds.
    }
  }

  removeRecent(id) {
    this.writeRecents(this.readRecents().filter((candidate) => candidate !== id));
  }
}

function normalizeType(type) {
  const value = String(type || "").toLowerCase();
  if (value === "indicator") return "Indicator";
  if (value === "chart") return "Chart";
  throw new TypeError("Template repository type must be Indicator or Chart.");
}

function ensureUnique(records, type, normalizedName, exceptId = null) {
  if (records.some((record) =>
    record.type === type
    && record.id !== exceptId
    && (record.normalizedName || normalizeTemplateName(record.name)) === normalizedName)) {
    throw new TemplateRepositoryError(
      "DuplicateName",
      "A template with this name already exists."
    );
  }
}

function owned(records, id, type) {
  const record = records.find((candidate) => candidate.id === id && candidate.type === type);
  if (!record) throw missing();
  return record;
}

function ensureVersion(record, expectedVersion) {
  if (record.version !== expectedVersion) {
    throw new TemplateRepositoryError(
      "ConcurrencyConflict",
      "The template changed in another session.",
      { currentVersion: record.version }
    );
  }
}

function summary(record) {
  const { payload, tenantId, userId, normalizedName, ...result } = record;
  return structuredClone(result);
}

function missing() {
  return new TemplateRepositoryError("MissingTemplate", "The template was not found.");
}

function createId() {
  return globalThis.crypto?.randomUUID?.()
    || `template-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function readJsonArray(storage, key) {
  try {
    const value = JSON.parse(storage?.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function templatePolicyMessage(code, fallback) {
  const normalized = String(code || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replaceAll("_", "-")
    .toLowerCase();
  if (normalized === "resource-product-mismatch") {
    return "This template belongs to another product.";
  }
  if (normalized === "enrollment-expired") return "Your product access has expired.";
  if (normalized === "product-inactive") return "This product is not currently available.";
  if (normalized === "product-access-denied" || normalized === "access-invalid") {
    return "You no longer have access to templates for this product.";
  }
  return fallback;
}
